import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Smartphone, Code, Zap, CheckCircle, ArrowRight, Users, Shield, Sparkles, Globe, Lock, Zap as ZapIcon, Youtube, Layers, X } from 'lucide-react';
import Hyperspeed from './components/Hyperspeed';

const V1_FEATURES = [
  {
    label: 'Balance checks',
    text: 'Helps confirm amounts so notifications match real money movement and fewer mistakes slip through.'
  },
  {
    label: 'Strong detection',
    text: 'Tuned to recognize payment-related texts as clearly as possible.'
  }
];

const V2_FEATURES = [
  {
    label: 'Default SMS app',
    text: 'You can set Twixo as the phone’s main SMS app so incoming texts are delivered the way Android expects.'
  },
  {
    label: 'Normal inbox',
    text: 'New messages still show in your usual SMS list and stay available to other apps that are allowed to read SMS.'
  },
  {
    label: 'Your server link',
    text: 'Sends JSON to a URL you choose, with a secret key (Bearer token) so only you can accept the data.'
  },
  {
    label: 'Runs in the open',
    text: 'A small ongoing notice (“SMS sync running”) so the system keeps the sync service active.'
  },
  {
    label: 'Retries on failure',
    text: 'If a send fails, it tries again later with longer waits in between until it works or you fix settings.'
  },
  {
    label: 'After restart',
    text: 'When the phone reboots, pending work is rescheduled and the service can start again if you had it on.'
  },
  {
    label: 'No double sends',
    text: 'Each message gets a unique fingerprint so the same text isn’t processed twice by accident.'
  }
];

function App() {
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const paymentMethods = [
    { name: 'bKash', icon: '/img/bkash.png', color: 'from-purple-500 to-purple-600' },
    { name: 'Nagad', icon: '/img/nagad.png', color: 'from-green-500 to-green-600' },
    { name: 'Rocket', icon: '/img/rocket.png', color: 'from-orange-500 to-orange-600' },
    { name: 'Upay', icon: '/img/upay.png', color: 'from-red-500 to-red-600' }
  ];

  useEffect(() => {
    if (!downloadModalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDownloadModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [downloadModalOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Hyperspeed Background */}
      <Hyperspeed />

      {/* Elegant Hero Section */}
      <section className="relative pt-12 pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <motion.div 
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-blue-200 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Sparkles className="w-4 h-4 text-blue-300" />
              <span>Free Payment Automation</span>
            </motion.div>
            
            <motion.h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Automate Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                Payments
              </span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-300">
                Instantly
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Twixo is a modern Bangladeshi payment automation solution that intelligently 
              reads payment SMS and integrates seamlessly with your existing systems. 
              <span className="text-blue-400 font-medium"> Completely free to use.</span>
            </motion.p>
            
            {/* Elegant Stats */}
            <motion.div 
              className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-400 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="font-medium">1000+ Users</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10">
                <Shield className="w-4 h-4 text-green-400" />
                <span className="font-medium">Secure & Reliable</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/5 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10">
                <ZapIcon className="w-4 h-4 text-yellow-400" />
                <span className="font-medium">Real-time Processing</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Elegant Download Buttons */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center mb-10"
          >
            <motion.button
              type="button"
              variants={fadeInUp}
              onClick={() => setDownloadModalOpen(true)}
              className="group bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25 flex items-center space-x-3 cursor-pointer border-0"
            >
              <Smartphone className="w-5 h-5" />
              <span>Download app</span>
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform duration-300" />
            </motion.button>
            
            <motion.a
              href="/twixo.zip"
              download="twixo.zip"
              variants={fadeInUp}
              className="group bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold py-3 px-8 rounded-xl border border-white/20 hover:border-white/40 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-white/25 flex items-center space-x-3 cursor-pointer"
            >
              <Code className="w-5 h-5" />
              <span>Download Source</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
            </motion.a>
          </motion.div>

          {/* Setup tutorial — embedded video */}
          <motion.div
            id="setup-tutorial"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.65 }}
            className="w-full max-w-4xl mx-auto mb-16 scroll-mt-24"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Youtube className="w-6 h-6 text-red-400" aria-hidden />
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Setup tutorial
              </h2>
            </div>
            <p className="text-gray-400 text-sm sm:text-base mb-6 max-w-xl mx-auto text-center leading-relaxed">
              Follow this walkthrough to get Twixo installed and configured on your device.
            </p>
            <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-black/40 shadow-2xl shadow-black/50 aspect-video">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/_1BqfE81Hno"
                title="Twixo setup tutorial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-center">
              <a
                href="https://youtu.be/_1BqfE81Hno"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-400/90 hover:text-red-300 underline underline-offset-2 transition-colors"
              >
                Open on YouTube
              </a>
            </p>
          </motion.div>

          {/* Elegant Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            <motion.div 
              className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-blue-400/30 transition-all duration-300 transform hover:-translate-y-1"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">Smart SMS Reading</h3>
              <p className="text-gray-300 leading-relaxed text-center">Intelligent SMS parsing that extracts payment information with high accuracy</p>
            </motion.div>
            
            <motion.div 
              className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-green-400/30 transition-all duration-300 transform hover:-translate-y-1"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Code className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">Easy Integration</h3>
              <p className="text-gray-300 leading-relaxed text-center">Simple source code for seamless system integration and customization</p>
            </motion.div>
            
            <motion.div 
              className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-purple-400/30 transition-all duration-300 transform hover:-translate-y-1"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 text-center">100% Free</h3>
              <p className="text-gray-300 leading-relaxed text-center">No hidden costs, completely free to use with full features</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Elegant Payment Methods Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900/50 to-black/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Supported Payment Methods
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Currently supporting the most popular Bangladeshi payment platforms with more coming soon
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {paymentMethods.map((method, index) => (
              <motion.div
                key={method.name}
                variants={fadeInUp}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 text-center transition-all duration-300 transform hover:-translate-y-2 border border-white/10 hover:border-white/20"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-20 h-20 mx-auto p-3 group-hover:scale-110 transition-transform duration-300 mb-4">
                  <img 
                    src={method.icon} 
                    alt={method.name} 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{method.name}</h3>
                <div className={`w-12 h-1 bg-gradient-to-r ${method.color} rounded-full mx-auto group-hover:w-16 transition-all duration-300`}></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Elegant Footer */}
      <footer className="bg-gradient-to-t from-gray-900 to-black text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-8">
              <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 p-1">
                <img 
                  src="/img/twixo.png" 
                  alt="Twixo Logo" 
                  className="w-full h-full object-contain bg-white rounded-lg"
                />
              </div>
              <span className="text-2xl font-bold text-white">
                Twixo
              </span>
            </div>
            
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Bangladeshi Personal Payment Automation - Completely Free
            </p>
            
            <div className="pt-8 border-t border-gray-800">
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-6">
                <div className="flex items-center space-x-2 text-gray-400">
                  <Globe className="w-4 h-4" />
                  <span>Made in Bangladesh</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <Lock className="w-4 h-4" />
                  <span>100% Secure</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <Zap className="w-4 h-4" />
                  <span>Lightning Fast</span>
                </div>
              </div>
              
              <p className="text-gray-500 text-sm mb-4">
                © 2025 Twixo. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Developed by{' '}
                <a 
                  href="https://sweez.xyz" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 transition-colors duration-200 underline"
                >
                  Sweez
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </footer>

      <AnimatePresence>
        {downloadModalOpen && (
          <motion.div
            key="download-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              aria-label="Close download dialog"
              onClick={() => setDownloadModalOpen(false)}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="download-modal-title"
              className="relative w-full max-w-4xl max-h-[min(90vh,900px)] overflow-y-auto rounded-2xl border border-white/15 bg-gray-950/95 shadow-2xl shadow-black/60"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-white/10 bg-gray-950/95 px-5 py-4 sm:px-6 backdrop-blur-md">
                <div className="text-left">
                  <h2 id="download-modal-title" className="text-xl sm:text-2xl font-bold text-white">
                    Choose your version
                  </h2>
                  <p className="text-gray-400 text-sm mt-1 max-w-xl">
                    V1 is what we recommend for most people. V2 is optional—use it if you need its features or as a backup.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDownloadModalOpen(false)}
                  className="shrink-0 rounded-lg p-2 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-blue-400/25 bg-white/[0.03] p-5 text-left flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-500/20 text-blue-300 text-sm font-bold">V1</span>
                    <h3 className="text-lg font-semibold text-white">Recommended</h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    Best for everyday use—reliable payment detection with extra balance checks for safety.
                  </p>
                  <ul className="space-y-2.5 text-gray-300 text-sm flex-1 mb-5">
                    {V1_FEATURES.map((f) => (
                      <li key={f.label} className="flex gap-2.5">
                        <CheckCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                        <span>
                          <span className="text-white/95 font-medium">{f.label}</span>
                          {' — '}
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/twixo.apk"
                    download="twixo.apk"
                    onClick={() => setDownloadModalOpen(false)}
                    className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-blue-500/25"
                  >
                    <Download className="w-5 h-5" />
                    Download V1
                  </a>
                </div>

                <div className="rounded-xl border border-amber-400/25 bg-white/[0.03] p-5 text-left flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500/20 text-amber-300 text-sm font-bold">V2</span>
                    <h3 className="text-lg font-semibold text-white">Optional / backup</h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    For advanced setup (e.g. default SMS app, server sync). May suit you as a second install or backup.
                  </p>
                  <ul className="space-y-2.5 text-gray-300 text-sm flex-1 mb-5 max-h-[min(40vh,320px)] lg:max-h-none overflow-y-auto pr-1">
                    {V2_FEATURES.map((f) => (
                      <li key={f.label} className="flex gap-2.5">
                        <CheckCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                        <span>
                          <span className="text-white/95 font-medium">{f.label}</span>
                          {' — '}
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/twixo-v2.apk"
                    download="twixo-v2.apk"
                    onClick={() => setDownloadModalOpen(false)}
                    className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold text-white bg-white/10 border border-amber-400/40 hover:bg-white/15 hover:border-amber-400/60 transition-all"
                  >
                    <Layers className="w-5 h-5 text-amber-300" />
                    Download V2
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
